import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import translation files directly for fallback
import enTranslations from '../locales/en';
import arTranslations from '../locales/ar';

const resources = {
  en: enTranslations,
  ar: arTranslations,
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Language settings
    lng: 'en', // Default to English (Arabic to be set up later)
    fallbackLng: 'ar',
    supportedLngs: ['en', 'ar'],
    
    // Debugging
    debug: process.env.NODE_ENV === 'development',
    
    // Resources
    resources,
    
    // Backend configuration for loading from public folder
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    // Language detection options
    detection: {
      order: [
        'localStorage', 
        'navigator', 
        'htmlTag', 
        'path', 
        'subdomain'
      ],
      lookupLocalStorage: 'autoservice_language',
      caches: ['localStorage'],
    },
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Namespace settings
    defaultNS: 'common',
    ns: [
      'common',
      'automotive',
      'navigation',
      'auth',
      'search',
      'profile',
      'posts',
      'services',
      'categories',
      'locations',
      'forms',
      'errors'
    ],
    
    // React options
    react: {
      useSuspense: false, // Disable suspense for SSR compatibility
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
    
    // Global configurations
    contextSeparator: '_',
    pluralSeparator: '_',
    keySeparator: '.',
    nsSeparator: ':',
    
    // Load missing keys in development
    saveMissing: process.env.NODE_ENV === 'development',
  });

// Set HTML direction based on language
i18n.on('languageChanged', (lng) => {
  const direction = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.dir = direction;
  document.documentElement.lang = lng;
  
  // Update body class for CSS styling
  document.body.className = document.body.className
    .replace(/\b(ltr|rtl)\b/g, '')
    .concat(` ${direction}`)
    .trim();
});

// Initialize direction on load
const initialDirection = i18n.language === 'ar' ? 'rtl' : 'ltr';
document.documentElement.dir = initialDirection;
document.documentElement.lang = i18n.language || 'en';
document.body.className = document.body.className
  .replace(/\b(ltr|rtl)\b/g, '')
  .concat(` ${initialDirection}`)
  .trim();

export default i18n;

// Export types for better TypeScript support
export interface TranslationKey {
  [key: string]: string | TranslationKey;
}

// Utility function for automotive-specific translations
export const getAutomotiveTranslation = (key: string, options?: any) => {
  // Use type assertion to handle i18next typing issue
  const i18nInstance = i18n as any;
  return options ? i18nInstance.t(`automotive:${key}`, options) : i18nInstance.t(`automotive:${key}`);
};

/**
 * Format currency with international support
 * @param amount - The amount to format
 * @param currency - Currency code (e.g., 'USD', 'EUR', 'SAR')
 * @param locale - Optional locale override
 */
export const formatCurrency = (amount: number, currency: string = 'USD', locale?: string) => {
  const userLocale = locale || i18n.language || 'en';
  try {
    return new Intl.NumberFormat(userLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if currency is not supported
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * @deprecated Use formatCurrency instead
 * Kept for backwards compatibility
 */
export const formatSaudiCurrency = (amount: number) => {
  return formatCurrency(amount, 'SAR', i18n.language === 'ar' ? 'ar-SA' : 'en');
};

/**
 * Format phone number for international display
 * Attempts to format based on common patterns
 * For production, consider using libphonenumber-js
 * @param phone - Phone number string
 * @param countryCode - Optional country code for formatting hints
 */
export const formatInternationalPhone = (phone: string, countryCode?: string): string => {
  // Remove any non-digits except leading +
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');
  
  if (!digits) return phone;
  
  // Common international formats
  const formatPatterns: Record<string, (d: string) => string> = {
    // Saudi Arabia: +966 5X XXX XXXX
    '966': (d) => `+966 ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8)}`,
    // UAE: +971 5X XXX XXXX
    '971': (d) => `+971 ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8)}`,
    // US/Canada: +1 XXX XXX XXXX
    '1': (d) => `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`,
    // UK: +44 XXXX XXXXXX
    '44': (d) => `+44 ${d.slice(2, 6)} ${d.slice(6)}`,
    // India: +91 XXXXX XXXXX
    '91': (d) => `+91 ${d.slice(2, 7)} ${d.slice(7)}`,
  };
  
  // Try to match known patterns
  for (const [prefix, formatter] of Object.entries(formatPatterns)) {
    if (digits.startsWith(prefix) && digits.length >= 10) {
      return formatter(digits);
    }
  }
  
  // Generic fallback: add + if it was there, group digits
  if (hasPlus || digits.length > 10) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`.trim();
  }
  
  // Local number fallback
  return digits.length > 6 
    ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    : phone;
};

/**
 * @deprecated Use formatInternationalPhone instead
 * Kept for backwards compatibility
 */
export const formatSaudiPhone = (phone: string) => {
  return formatInternationalPhone(phone, 'SA');
};
